import axios, { isAxiosError } from "axios";

import { requestPost } from "../request";

export type FileUploadOptions = {
  createUrlPath: string;
  confirmUploadPath: string;
  companyId?: number;
  id?: number;
  applicationStage?: "underwriting" | "prescreen";
  documentType?: string;
  requestFn?: typeof requestPost;
  onProgress?: (progress: number) => void;
};

/**
 * request to our backend asking for a presigned post URL for S3
 * file name, size and type are included and in the backend which can be added
 * as arguments to the presigned post URL
 */
export const createUploadUrl = (
  { name, size, type }: File,
  path: string,
  companyId?: number,
  id?: number,
  applicationStage?: "underwriting" | "prescreen",
  documentType?: string,
  requestFn = requestPost
) =>
  requestFn(path, {
    companyId,
    id,
    name,
    size,
    type,
    application_stage: applicationStage,
    document_type: documentType,
  });

/**
 * multipart upload to S3 using Axios (supports onUploadProgress for progress reporting)
 * urlOptions has a bunch of AWS Specific fields (e.g. access key, signatures)
 * that come back from the create_presigned_post_url method in the backend
 */
export const postFile = (
  file: File,
  urlOptions: { url: string; fields: Record<string, string> },
  onProgress?: (progress: number) => void
): Promise<{ ok: boolean; status: number; statusText: string }> => {
  const form = new FormData();
  Object.keys(urlOptions.fields).forEach((key) => form.append(key, urlOptions.fields[key]));
  form.append("Content-type", file.type);
  form.append("file", file);

  return axios
    .post(urlOptions.url, form, {
      headers: {},
      onUploadProgress: (e) => {
        if (onProgress) {
          const value = e.total && e.total > 0 ? Math.round((e.loaded / e.total) * 100) : e.loaded > 0 ? 100 : 0;
          onProgress(value);
        }
      },
    })
    .then((response) => {
      onProgress?.(100);
      return {
        ok: response.status >= 200 && response.status < 300,
        status: response.status,
        statusText: response.statusText ?? "",
      };
    })
    .catch((error) => {
      if (isAxiosError(error) && error.response) {
        return {
          ok: false,
          status: error.response.status,
          statusText: error.response.statusText ?? "",
        };
      }
      throw error;
    });
};

// confirm that an upload to S3 was successful
export const confirmUpload = (confirmUploadPath, objectKey, id, requestFn = requestPost) =>
  requestFn(confirmUploadPath, { objectKey, id });

/**
 * Full process for uploading document
 *
 * 1. Request to the backend to get a presigned POST url.
 * 2. Upload file to S3 using the presigned URL.
 * 3. Request to the backend to confirm that the file was uploaded.
 */
export const uploadDocument = async (
  file: File,
  {
    createUrlPath,
    confirmUploadPath,
    companyId,
    id,
    applicationStage,
    documentType,
    requestFn = requestPost,
    onProgress,
  }: FileUploadOptions
) => {
  const { objectKey, urlOptions } = await createUploadUrl(
    file,
    createUrlPath,
    companyId,
    id,
    applicationStage,
    documentType,
    requestFn
  );

  if (!objectKey) {
    throw new Error("createUploadUrl result does not have an object key");
  }

  const uploadResult = await postFile(file, urlOptions, onProgress);

  if (!uploadResult.ok) {
    throw new Error(`postFile result has status: ${uploadResult.status}: ${uploadResult.statusText}`);
  }

  return confirmUpload(confirmUploadPath, objectKey, id, requestFn);
};
